import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useBillingService } from "../services/api/billing-service";
import {
  ISubscriptionFrontend,
  IUpdateSubscriptionPaymentRequest,
  IUpdateSubscriptionPaymentResponse,
  ICreateCheckoutSessionSetupResponse,
  ICheckoutSessionDetails,
} from "../types/api/billing";

export function useBillingSubscriptions() {
  const service = useBillingService();
  return useQuery<ISubscriptionFrontend[]>({
    queryKey: ["billing", "subscriptions"],
    queryFn: () => service.fetchSubscriptions(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useUpdateSubscriptionPayment() {
  const service = useBillingService();
  const queryClient = useQueryClient();
  return useMutation<
    IUpdateSubscriptionPaymentResponse,
    Error,
    { subscriptionId: string; payload: IUpdateSubscriptionPaymentRequest }
  >({
    mutationFn: ({ subscriptionId, payload }) =>
      service.updateSubscriptionPayment(subscriptionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "subscriptions"] });
    },
  });
}

// Embedded Checkout (setup mode)
export function useCreateEmbeddedCheckoutSetupSession() {
  const service = useBillingService();
  return useMutation<
    ICreateCheckoutSessionSetupResponse,
    Error,
    { customerId: string }
  >({
    mutationFn: ({ customerId }) =>
      service.createEmbeddedCheckoutSetupSession(customerId),
  });
}

export function useCheckoutSessionDetails(id?: string) {
  const service = useBillingService();
  return useQuery<ICheckoutSessionDetails | null>({
    queryKey: ["billing", "checkout-session", id],
    queryFn: async (): Promise<ICheckoutSessionDetails | null> =>
      id ? service.getCheckoutSession(id) : null,
    enabled: Boolean(id),
    staleTime: 60 * 1000,
  });
}
